import { useGraphCacheStore } from '@tunasong/graph-cache'
import type { Entity } from '@tunasong/schemas'

/**
 * Unconfirmed graph-cache patches already owned by a buffered or in-flight save, so a second hook instance on
 * the same entity cannot confirm them before the save that carries them resolves. GH #1966
 */
export const claimedSeqs = new Set<number>()

/**
 * The graph-cache patch per entity that keeps my held edit showing while a conflict is held (GH #1912 option B).
 * A settled save confirms the patches it carried, which would let the server's version, theirs, show through
 * while the notice says mine is not saved yet. My latest held edit is pinned instead, as one patch of its own.
 */
const heldPins = new Map<string, number>()

export const hasHeldPin = (entityId: string) => heldPins.has(entityId)

/** Pin my held edit over the server's version, replacing an earlier pin; no edit releases the pin */
export const pinHeldEdit = (entityId: string, mine?: Partial<Entity>) => {
  const cache = useGraphCacheStore.getState()
  const pinned = heldPins.get(entityId)
  if (pinned !== undefined) {
    cache.confirmOptimistic(entityId, [pinned])
    claimedSeqs.delete(pinned)
    heldPins.delete(entityId)
  }
  if (!mine || !cache.optimisticUpdateCache(entityId, mine)) {
    return
  }
  const seq = cache.lastOptimisticSeq(entityId)
  if (seq !== undefined) {
    /** Claimed, so no save of another edit confirms it */
    claimedSeqs.add(seq)
    heldPins.set(entityId, seq)
  }
}
